import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { addToCart } from '../store/slices/cartSlice';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import Layout from '../components/Layout';
import PageMeta from '../components/PageMeta';
import AvailabilityBadge from '../components/AvailabilityBadge';
import SearchBar from '../components/SearchBar';
import { fetchProducts } from '../services/products';
import { getStorageUrl } from '../services/api';
import './Products.css';

const ITEMS_PER_PAGE = 12;

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getImageUrl = (image) => getStorageUrl(image);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const searchParams = new URLSearchParams(location.search);
      const q = searchParams.get('search') || '';

      const params = {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        status: 'active'
      };
      if (q.trim()) params.search = q.trim();
      
      const data = await fetchProducts(params);
      setProducts(data.data || []);
      setTotalPages(data.last_page ?? 1);
      setTotal(data.total ?? 0);
    } catch (err) {
      setError(err.message || 'Failed to load products.');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, location.search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [location.search]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleProductClick = (productId) => {
    navigate(`/products/${productId}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const descriptionSnippet = (product) => {
    const text = product.subtitle || product.description || '';
    if (typeof text !== 'string') return '';
    const t = text.trim();
    return t.length <= 80 ? t : t.slice(0, 80) + '…';
  };

  return (
    <Layout>
      <PageMeta
        title="Products"
        description="Browse quality agricultural and greenhouse products from FM Ultratech Farms. Seeds, equipment, and supplies for modern farming in Nigeria."
      />
      <div className="products-page">
        <div className="products-hero">
          <div className="products-hero-content">
            <h1>Our Products</h1>
            <p>High-Quality Agricultural Solutions</p>
          </div>
        </div>
        
        <SearchBar />

        <div className="products-content">
          <div className="products-container">
            {error && (
              <div className="products-list-error">{error}</div>
            )}

            {(loading && products.length === 0) ? (
              <div className="loading">Loading products...</div>
            ) : (
              <>
                {products.length === 0 ? (
                  !error && (
                    <div className="products-empty">
                      {new URLSearchParams(location.search).get('search')
                        ? 'No products match your search.'
                        : 'No products available at the moment.'}
                    </div>
                  )
                ) : (
                  <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
                    {total > 0 && (
                      <p className="products-count">
                        Showing page {currentPage} of {totalPages} ({total} product{total !== 1 ? 's' : ''})
                      </p>
                    )}
                    <div className="products-grid">
                      {products.map((product) => (
                        <div key={product.id} className="product-card">
                          <div
                            className="product-image"
                            onClick={() => handleProductClick(product.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            {getImageUrl(product.image) ? (
                              <img src={getImageUrl(product.image)} alt={product.name} />
                            ) : (
                              <div className="product-image-placeholder">No image</div>
                            )}
                            <div className="product-badges">
                              {product.featured && (
                                <span className="product-featured-badge">Featured</span>
                              )}
                              <AvailabilityBadge availabilityType={product.availability_type} size="small" />
                            </div>
                          </div>
                          <div onClick={() => handleProductClick(product.id)} style={{ cursor: 'pointer' }}>
                            <h3 className="product-title">{product.name}</h3>
                            <p className="product-description">{descriptionSnippet(product)}</p>
                            <p className="product-price">{formatNaira(toDisplayNaira(product.price, nairaPerYuan))}</p>
                          </div>
                          <div className="product-card-actions">
                            <button
                              type="button"
                              className="add-to-cart-btn-small"
                              onClick={(e) => {
                                e.stopPropagation();
                                dispatch(addToCart({ product, quantity: 1 }));
                                alert('Product added to cart!');
                              }}
                            >
                              Add to Cart
                            </button>
                            <button
                              type="button"
                              className="view-details-btn"
                              onClick={() => handleProductClick(product.id)}
                            >
                              View Details &gt;
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="pagination">
                        <button
                          type="button"
                          className="pagination-btn"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                        <div className="pagination-numbers">
                          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                            let page;
                            if (totalPages <= 10) page = i + 1;
                            else if (currentPage <= 5) page = i + 1;
                            else if (currentPage >= totalPages - 4) page = totalPages - 9 + i;
                            else page = currentPage - 5 + i;
                            return (
                              <button
                                key={page}
                                type="button"
                                className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="pagination-btn"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Products;
